<template>
  <div  v-if="bomLoaded && items.length"
        :class="[layout, 'bomContainer']">
    <nav  v-if="showSearchBar"
          class="navbar" style="border-right: 1px solid lightgrey;">
      <search-type-ahead :placeholder="$t('searchInContent', { contentName: searchContentNameLabel })"
                         :is-bom="true"
                         :clear-on-enter="false"
                         :content="content"
                         :limit="5"
                         :suggestion-types="['Part']"
                         :value="bomQuery"
                         :show-filtered-count="true"
                         :total-count="effectiveBomItemsCount"
                         :total-displayed="searchFilteredBomItemsCount"
                         class="bom-search"
                         @enter="search"
                         @clearClick="clearClick"
      >
        <p v-if="hasBomQuery && canNavigateResults"
           class="control">
          <button class="button is-primary is-outlined previous-button"
                  @click="previousResult">
            <span class="icon is-medium">
              <i class="fa fa-chevron-left" />
            </span>
          </button>
        </p>
        <p v-if="hasBomQuery && canNavigateResults"
           class="control">
          <button class="button is-primary is-outlined next-button"
                  @click="nextResult">
            <span class="icon is-medium">
              <i class="fa fa-chevron-right" />
            </span>
          </button>
        </p>
      </search-type-ahead>
    </nav>
    <div class="bomWrapper"
         :class="{ 'widget-font': isWidget }">
      <table class="table is-bordered is-striped is-narrow is-hoverable is-marginless">
        <thead>
        <tr>
          <bom-header v-for="column in columns"
                      :key="column.property"
                      :column="column"
                      @togglePrice="togglePriceData($event.property)">
            <div v-if="isMobile" style="margin-left: -1em">
              <p class="control">
                <a  data-int="search-button"
                    class="button is-primary is-small"
                    @click="isSearchOpen = !isSearchOpen">
                    <span class="icon">
                      <i class="fa fa-search" />
                    </span>
                </a>
              </p>
            </div>
          </bom-header>
        </tr>
        </thead>

        <tbody class="table-body"
               :key="bomInstanceKey">
        <tr v-for="bomItem in effectiveBomItemsToShow"
            :id="bomItem.pagePartId"
            :key="bomItem.pagePartId"
            :class="{'is-selected': isSelected(bomItem)}"
            class="bom-item"
            @click="handleSelection(bomItem, $event)"
            @click.meta="handleCommandClickSelection(bomItem, $event)">
          <bom-cell v-for="column in columns"
                    class="bom-cell"
                    :key="bomItem.id + '-' + column.property"
                    :add-to-cart-qty-behavior="addToCartQtyBehavior"
                    :bom-item="bomItem"
                    :column="column"
                    :hide-retail-price="hideRetailPrice"
                    :hide-discount-price="hideDiscountPrice"
                    :hide-whole-sale-price="hideWholeSalePrice"
                    :has-cart-prompt-for-qty-enabled="hasCartPromptForQtyEnabled"
                    :has-enabled-widget-comments="hasEnabledWidgetComments"
                    :has-enabled-widget-tags="hasEnabledWidgetTags"
                    :is-library-beta-user="isLibraryBetaUser"
                    :is-mobile="isMobile"
                    :is-widget="isWidget"
                    :is-widget-cart-enabled="isWidgetCartEnabled"
                    :has-widget-info-landing-page="hasWidgetInfoLandingPage"
                    :item-label="itemLabel(bomItem)"
                    :hotpoint="getHotpointDtoForBomItem(bomItem)"
                    :popover-tags="popoverTags"
                    :popover-comments="popoverComments"
                    @openHotpointLink="openHotpointLink"
                    @openHotpointPopover="val => openHotpointPopover({ bomItem: bomItem, close: val })"
                    @openThumbnailPopover="openThumbnailPopover"
                    @closeThumbnailPopover="closeThumbnailPopover"
                    @openTagsPopover="openTagsPopover"
                    @closeTagsPopover="closeTagsPopover"
                    @openCommentsPopover="openCommentsPopover"
                    @closeCommentsPopover="closeCommentsPopover"
                    @widgetAddToCart="emitWidgetAddToCartEvent"
                    @openWidgetPartInfo="openWidgetPartInfo" />
        </tr>
        </tbody>
      </table>
    </div>
  </div>
  <div v-else-if="!bomLoaded"
       class="loading">
    <loading-icon />
  </div>
</template>

<script>
import LoadingIcon from '@/components/common/LoadingIcon'
import SearchTypeAhead from '@/components/common/search/SearchTypeAhead'
import BomCell from './cells/BomCell'
import BomHeader from './headers/BomHeader'
import partLocationUtil from '@/components/common/partLocationUtil'
import { computed } from 'vue'
import { mapState, mapActions, mapGetters, useStore } from 'vuex'
import { useInfoLandingPage } from '@/components/widgets/composables/useInfoLandingPage'
import { useOpenToContent } from '@/components/widgets/composables/useOpenToContent'
import { useWidgetAddToCart } from '@/components/widgets/composables/useWidgetAddToCart'
import { v4 as uuid } from 'uuid'
import { WidgetTypes } from '@/interfaces/global'

export default {
  name: 'Bom',
  components: {
    BomCell,
    BomHeader,
    LoadingIcon,
    SearchTypeAhead
  },
  setup() {
    const { emitWidgetAddToCartEvent } = useWidgetAddToCart()
    const {
      hasHotpointLinkInBook,
      isSelectingBomItemViaWidgetConfig,
      setIsSelectingBomItemViaWidgetConfig,
      widgetOpenHotpointLink,
      tocQuery
    } = useOpenToContent()
    const { openPartInfoPage: openWidgetPartInfo } = useInfoLandingPage()

    const store = useStore()
    const searchContentNameLabel = computed(() => (store.state.content?.name?.length > 25) ? store.state.content?.name.substring(0,25) : store.state.content?.name)

    return {
      emitWidgetAddToCartEvent,
      hasHotpointLinkInBook,
      isSelectingBomItemViaWidgetConfig,
      openWidgetPartInfo,
      setIsSelectingBomItemViaWidgetConfig,
      widgetOpenHotpointLink,
      searchContentNameLabel,
      widgetTocQuery: tocQuery
    }
  },
  props: {
    isMobile: {
      type: Boolean,
      required: false
    }
  },
  data () {
    return {
      bomInstanceKey: 0,
      bomSearchRequestId: 0,
      hasSeededTocQuery: false,
      effectiveBomItems: [], // Enriched bom data for viewing
      highlightedInstance: 0,
      // Reduce computed props
      addToCartQtyBehavior: this.$store.state.user.preferences.addToCartQtyBehavior,
      isSearchOpen: false,
      isMounting: true
    }
  },
  watch: {
    items () {
      /* In widgets at least, there's a race condition issue
       * with non-immediate watchers firing before mounted hook
       * code. The root cause is unknown but could be a result
       * of the Vue 2 migration tool.
       */
      if (this.isMounting) return

      if (this.items.length) {
        if (!this.effectiveBomItems?.length) {
          this.preprocessBomItemData()
        }
        if (this.bomQuery?.length) {
          this.search(this.bomQuery, { fromToc: true })
        }
      }
    },
    //watcher
    bomQuery(value) {
      if (this.isMounting) return
      if (!this.hasSeededTocQuery) return
      if (value === '' && this.bomQuery !== '') return
      if (this.items?.length) {
        this.search(this.bomQuery, { fromToc: false })
      }
    },
    unprocessedCart: {
      immediate: true,
      handler (value, oldValue) {
        if (JSON.stringify(value) !== JSON.stringify(oldValue)) {
          this.preprocessBomItemData()
        }
      }
    },
    selected (newVals, oldVals) {
      if (!newVals || newVals.length === 0) {
        this.openThumbnailPopover()
        return
      }
      const newValues = newVals ? Array.from(newVals) : null
      const oldValues = oldVals ? Array.from(oldVals) : null

      const arraysIdentical = newValues && oldValues &&
        newValues.length === oldValues.length &&
        newValues.every((id, index) => id === oldValues[index])

      if (arraysIdentical) {
        this.openSelectedThumbnailPopover(newValues[0])
        return
      }
      // Find the selected row and scroll it into view
      const selectedItem = newValues[0]
      if (selectedItem?.pagePartId) {
        const selectedRow = document.getElementById(selectedItem.pagePartId)
        if (selectedRow) {
          selectedRow.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          })
        }
      }
      this.openSelectedThumbnailPopover(selectedItem)
    }
  },
  computed: {
    ...mapGetters({
      autoOpenPartThumbnailPreviewForLibrary: 'user/autoOpenPartThumbnailPreview',
      autoOpenPartThumbnailPreviewForWidget: 'user/autoOpenPartThumbnailPreviewEnabled',
      hasCartPromptForQtyEnabled: 'user/hasCartPromptForQtyEnabled',
      hasEnabledWidgetComments: 'widgets/hasEnabledComments',
      hasEnabledWidgetTags: 'widgets/hasEnabledTags',
      hasWidgetInfoLandingPage: 'widgets/hasInfoLandingPage',
      hotpointsFlatList: 'content/hotpointsFlatList',
      isCartOrErpCartEnabled: 'user/isCartOrErpCartEnabled',
      isLibraryBetaUser: 'user/enableLibraryBetaFeature',
      isWidgetCartEnabled: 'widgets/isWidgetCartEnabled',
      popoverTags: 'content/tagNamesToValuesList',
      widgetType: 'widgets/widgetType',
      allowMultiSelect: 'content/allowMultiSelect'
    }),
    autoOpenThumbnailPreviewEnabled () {
      return this.isWidget
        ? this.autoOpenPartThumbnailPreviewForWidget
        : this.autoOpenPartThumbnailPreviewForLibrary
    },
    ...mapState({
      bomItems: (state) => state.content.bom.items,
      bomLoaded: (state) => state.content.bom.isLoaded,
      columns: (state) => state.content.bom.columns.items,
      content: (state) => state.content,
      hideDiscountPrice: (state) => state.user.pricing.hideDiscountPrice,
      hideRetailPrice: (state) => state.user.pricing.hideRetailPrice,
      hideWholeSalePrice: (state) => state.user.pricing.hideWholeSalePrice,
      isWidget: ({ auth }) => auth.isWidget,
      items: (state) => state.content.bom.items,
      layout: (state) => state.content.bom.layout,
      popoverComments: ({ widgets }) => widgets.partComments.items,
      selected: (state) => state.content.bom.selected,
      unprocessedCart: (state) => state.cart.unprocessedItems
    }),
    bomQuery: {
      get () {
        const q = this.$store.state.search?.filters?.bomQuery
        return (typeof q === 'string') ? q : ''
      },
      set (value) {
        const next = (typeof value === 'string') ? value : ''
        if (!next) {
          this.$store.dispatch('search/clearBomQuery')
          return
        }
        this.$store.dispatch('search/setBomQuery', next)
      }
    },
    //computed
    tocQuery() {
      const stored = this.$store.state.search?.filters?.tocQuery
      return (typeof stored === 'string' ? stored : '') || ''
    },
    isMediaWidget() {
      return (this.widgetType === WidgetTypes.media)
        && this.isWidget
    },
    isPageWidget() {
      return (this.widgetType === WidgetTypes.page)
        && this.isWidget
    },
    effectiveBomItemsToShow() {
      const childrenToHide = this.searchFilteredBomItems.reduce((map, it) => {
        if (it.isCollapsed && !!it.children.length) {
          it.children.forEach(child => map.set(child, child))
        }
        return map
      }, new Map())
      return this.searchFilteredBomItems.filter(it => !childrenToHide.has(it.refKey))
    },
    effectiveBomItemsRefKeyToIndex() {
      return this.effectiveBomItems.reduce((map, item, idx) => {
        map.set(item.refKey, idx)
        return map
      }, new Map())
    },
    bomToParentAndChildrenMap() {
      return this.items.reduce((map, it, i, array) => {
        const parent = it.refKey
        const children = []
        const itemIndent = it.indention
        const truncatedBom = array.slice(i + 1)

        for (let index = 0; index < truncatedBom.length; index++) {
          const { indention, refKey } = truncatedBom[index]
          const isSibling = indention === itemIndent
          const isChild = itemIndent === (indention - 1)
          if (isSibling) break
          else if (isChild) { children.push(refKey) }
        }

        map.set(parent, map.has(parent)
          ? { ...map.get(parent), children }
          : { children } )
        children.forEach(child => {
          map.set(child, map.has(child)
            ? { ...map.get(child), parent }
            : { parent })
        })
        return map
      }, new Map())
    },
    showSearchBar() {
      return (this.isMobile && this.isSearchOpen) || !this.isMobile
    },
    searchFilteredBomItems () {
      if (this.bomQuery?.length > 0) {
        return this.effectiveBomItems.filter(b => b.isInSearchResults === true)
      } else {
        return this.effectiveBomItems
      }
    },
    searchFilteredBomItemsCount () {
      return this.searchFilteredBomItems?.length ?? 0
    },
    effectiveBomItemsCount () {
      return this.effectiveBomItems?.length ?? 0
    },
    canNavigateResults () {
      return (this.effectiveBomItemsToShow?.length ?? 0) > 1
    },
    hasBomQuery () {
      return this.bomQuery?.length
    }
  },
  methods: {
    ...mapActions({
      select: 'content/selectBomItems',
      selectBomItemsFromCallout: 'content/selectBomItemsFromCallout',
      clearBomItemSelection: 'content/clearBomItemSelection',
      getBomSearchWithReturn: 'content/bomSearchWithReturn'
    }),
    getHotpointDtoForBomItem(bomItem) {
      for (const hotpointDto of this.hotpointsFlatList) {
        if (hotpointDto.item === bomItem.itemText) {
          return hotpointDto
        }
      }
      return {}
    },
    handleSelection(bomItem, event) {
      if (event.metaKey) {
        return
      }
      const isDropdownCaret = event.target.classList.value.includes("caret")
      if (isDropdownCaret) {
        this.expandOrCollapseItem(bomItem)
      } else {
        this.select({item: bomItem, append: false})
        if (!this.isPartInfoIndicatorClick(event)) {
          this.openSelectedThumbnailPopover(bomItem)
        }
      }
      this.closeHotpointInfo(bomItem.refKey)
    },
    isPartInfoIndicatorClick(event) {
      return event.target.closest?.('.indicators')
    },
    handleCommandClickSelection(bomItem, event) {
      event.preventDefault()
      event.stopPropagation()
      if (!this.allowMultiSelect) return

      // Multi-select mode - toggle this item while preserving others
      this.select({item: bomItem, append: true})
      this.closeHotpointInfo(bomItem.refKey)
    },
    itemLabel(bomItem) {
      return (bomItem.overrideItemText || !bomItem.itemText)
        ? this.$t('itemNotShown')
        : bomItem?.itemText ?? ''
    },
    expandOrCollapseItem(bomItem) {
      const idx = this.effectiveBomItems.findIndex(it => it.refKey === bomItem.refKey)
      const { isCollapsed } = bomItem
      setIsCollapsed.call(this, idx, !isCollapsed)
      for (let index = idx + 1; index < this.effectiveBomItems.length; index++) {
        const element = this.effectiveBomItems[index];
        const isSibling = element.indention === bomItem.indention
        if (isSibling) break
        else if (isCollapsed) continue
        else setIsCollapsed.call(this, index, !isCollapsed)
      }

      function setIsCollapsed(idx, isCollapsed) {
        const oldItem = this.effectiveBomItems[idx]
        const updatedItem = { ...oldItem, isCollapsed }
        this.effectiveBomItems.splice(idx, 1, updatedItem)
      }
    },
    openHotpointPopover (val) {
      const hotpoint = this.getHotpointDtoForBomItem(val.bomItem)
      if (hotpoint?.link?.length === 1) {
        this.openHotpointLink(hotpoint.link[0])
      } else {
        const index = this.effectiveBomItemsRefKeyToIndex.get(val.bomItem.refKey)
        const isValidItem = (index >= 0)
        if(!val.close && isValidItem){
          this.effectiveBomItems[index].showHotpointLinkPopover = true
        } else if (isValidItem) {
          this.effectiveBomItems[index].showHotpointLinkPopover = false
        }
        this.closeHotpointInfo(val.bomItem.refKey)
      }
    },
    openThumbnailPopover (refKey) {
      this.effectiveBomItems.forEach((bomItem, index) => {
        const showThumbnailPopover = bomItem.refKey === refKey && !!bomItem.thumbnailUrl
        if (
          bomItem.showThumbnailPopover !== showThumbnailPopover ||
          bomItem.showTagsPopover ||
          bomItem.showCommentsPopover
        ) {
          this.effectiveBomItems.splice(index, 1, {
            ...bomItem,
            showThumbnailPopover,
            showTagsPopover: false,
            showCommentsPopover: false
          })
        }
      })
    },
    closeThumbnailPopover (refKey) {
      const index = this.effectiveBomItemsRefKeyToIndex.get(refKey)
      if (index >= 0) {
        this.effectiveBomItems[index].showThumbnailPopover = false
      }
    },
    findEffectiveBomItemRefKey (bomItem) {
      if (!bomItem) {
        return null
      }
      if (bomItem.refKey && this.effectiveBomItemsRefKeyToIndex.has(bomItem.refKey)) {
        return bomItem.refKey
      }
      const item = this.effectiveBomItems.find(item => {
        if (item.pagePartId != null && bomItem.pagePartId != null) {
          return String(item.pagePartId) === String(bomItem.pagePartId)
        }
        if (item.instanceId != null && bomItem.instanceId != null) {
          return String(item.instanceId) === String(bomItem.instanceId)
        }
        return false
      })
      return item?.refKey ?? null
    },
    openSelectedThumbnailPopover (selectedItem) {
      if (!this.autoOpenThumbnailPreviewEnabled) {
        return
      }
      setTimeout(() => {
        this.openThumbnailPopover(this.findEffectiveBomItemRefKey(selectedItem))
      })
    },
    openTagsPopover(refKey) {
      const index = this.effectiveBomItemsRefKeyToIndex.get(refKey)
      if (index >= 0) {
        this.effectiveBomItems[index] = {
          ...this.effectiveBomItems[index],
          showTagsPopover: true,
          showThumbnailPopover: false,
          showCommentsPopover: false
        }
      }
    },
    closeTagsPopover(refKey) {
      const index = this.effectiveBomItemsRefKeyToIndex.get(refKey)
      if (index >= 0) {
        this.effectiveBomItems[index].showTagsPopover = false
      }
    },
    openCommentsPopover(refKey) {
      const index = this.effectiveBomItemsRefKeyToIndex.get(refKey)
      if (index >= 0) {
        this.effectiveBomItems[index] = {
          ...this.effectiveBomItems[index],
          showCommentsPopover: true,
          showTagsPopover: false,
          showThumbnailPopover: false,

        }
      }
    },
    closeCommentsPopover(refKey) {
      const index = this.effectiveBomItemsRefKeyToIndex.get(refKey)
      if (index >= 0) {
        this.effectiveBomItems[index].showCommentsPopover = false
      }
    },
    closeHotpointInfo (selectedRefKey) {
      this.effectiveBomItems.forEach(bomItem => {
        if (bomItem.refKey !== selectedRefKey) {
          bomItem.showHotpointLinkPopover = false
        }
      })
    },
    openHotpointLink (link) {
      if (this.isWidget) {
        this.$store.dispatch('search/clearBomQuery')
        this.widgetOpenHotpointLink(link)
        return
      }

      if (link.type === 'page') {
        const element = document.getElementById(`${link.type}:${link.id}`)
        if (element) {
          element.click()
          return
        }
      }
      this.$store.dispatch('content/navigateToContent', { content: link })
    },
    togglePriceData (column) {
      let property
      switch (column) {
        case 'discounted':
          property = 'hideDiscountPrice'
          break
        case 'retail':
          property = 'hideRetailPrice'
          break
        case 'wholesale':
          property = 'hideWholeSalePrice'
          break
      }

      this.effectiveBomItems.forEach((item) => {
        item[property] = !item[property]
      })
      this.bomInstanceKey++
    },
    async search (query = '', { fromToc = false } = {}) {
      //ensure this is the same request and return otherwise
      const requestId = ++this.bomSearchRequestId
      const searchResults = await this.getBomSearchWithReturn({ query })

      if (requestId !== this.bomSearchRequestId) return

      let bomItemCount = 0
      let singleMatchItem = null

      this.effectiveBomItems.forEach((item) => {
        item.isCollapsed = false
        item.isInSearchResults = searchResults.some(
          (result) => {
            if (result.entityId === item.id) {
              bomItemCount++
              singleMatchItem = item
            }
            return result.entityId === item.id
          }
        )
      })
      this.bomInstanceKey++

      if (fromToc && query && bomItemCount === 0) {
        this.bomQuery = ''
        this.effectiveBomItems.forEach(item => {
          item.isInSearchResults = false
        })
        this.bomInstanceKey++
        return
      }

      this.bomQuery = query ? query : ''

      if (query && bomItemCount === 1 && singleMatchItem) {
        this.select({ item: singleMatchItem, append: false })
      }
    },
    nextResult () {
      if (!this.canNavigateResults) return

      const results = [...this.effectiveBomItemsToShow]
        .filter(it => it?.instanceId != null)
        .sort((a, b) => a.instanceId - b.instanceId)

      if (results.length < 2) return

      const selectedPagePartId = this.selected?.[0]?.pagePartId
      const currentIndex = selectedPagePartId
        ? results.findIndex(it => it.pagePartId === selectedPagePartId)
        : -1

      const nextIndex = currentIndex >= 0
        ? (currentIndex + 1) % results.length
        : 0

      const nextItem = results[nextIndex]
      if (nextItem) this.select({ item: nextItem, append: false })

      const active = document.activeElement
      if (active && typeof active.blur === 'function') active.blur()
    },
    previousResult () {
      if (!this.canNavigateResults) return

      const results = [...this.effectiveBomItemsToShow]
        .filter(it => it?.instanceId != null)
        .sort((a, b) => a.instanceId - b.instanceId)

      if (results.length < 2) return

      const selectedPagePartId = this.selected?.[0]?.pagePartId
      const currentIndex = selectedPagePartId
        ? results.findIndex(it => it.pagePartId === selectedPagePartId)
        : -1

      const prevIndex = currentIndex >= 0
        ? (currentIndex - 1 + results.length) % results.length
        : (results.length - 1)

      const prevItem = results[prevIndex]
      if (prevItem) this.select({ item: prevItem, append: false })

      const active = document.activeElement
      if (active && typeof active.blur === 'function') active.blur()
    },
    isSelected (bomItem) {
      if (this.selected && this.selected.length) {
        let index = -1
        this.selected.some((item, i) => {
          if (item.pagePartId === bomItem.pagePartId) {
            index = i
            return true
          }
          return false
        })
        return index >= 0
      }
      return false
    },
    preprocessBomItemData () {
      const bomItems = [...this.items]
      const hotpoints = this.hotpointsFlatList.map(hp => {
        // Hotpoint Link AKA hp.link type:
        // Array<{id: number, name: string, type: string}>?
        if (this.isPageWidget) {
          delete hp.link
        }
        else if (this.isMediaWidget) {
          const filteredLinks = hp.link?.filter(it => {
            return this.hasHotpointLinkInBook(it)
          }) ?? []
          if (!!filteredLinks.length) {
            hp.link = filteredLinks
          } else {
            delete hp.link
          }
        }
        return hp
      })

      const partLocation = partLocationUtil.getPartLocationData(this.$route.path)
      const partIdsInCart = this.unprocessedCart.map((cartItem) => cartItem.partId)

      bomItems.forEach(bi => {
        bi.refKey = uuid()
        const hotpoint = hotpoints.find(hp => hp.item === bi.itemText)
        bi.hasHotpointLink = !!hotpoint &&
          Object.prototype.hasOwnProperty.call(hotpoint, 'link')
        bi.hideDiscountPrice = this.hideDiscountPrice
        bi.hideRetailPrice = this.hideRetailPrice
        bi.hideWholeSalePrice = this.hideWholeSalePrice
        bi.isOrderable = !(bi.partNumber && bi.orderable) ? false
          : this.isWidget ? this.isWidgetCartEnabled
            : this.isCartOrErpCartEnabled
        bi.pagePartUrl = bi.pagePartId
          ? `${this.$route.path}/pagepart:${bi.pagePartId}`
          : `${this.$route.path}/${this.bomItem.type}:${this.bomItem.id}`

        if (partIdsInCart.includes(bi.id)) {
          if (this.unprocessedCart.length) {
            bi.isInCart = true
            if (this.unprocessedCart.some((ci) => {
                return ci.partId === bi.id &&
                  ci?.mediaId === partLocation?.bookId &&
                  ci?.chapterId === partLocation?.chapterId &&
                  ci?.subChapterId === partLocation?.subChapterId &&
                  ci?.pageId === partLocation?.pageId
              }
            )) {
              bi.isExactInCart = true
            }
          }
        } else {
          bi.isInCart = false
          bi.isExactInCart = false
        }
      })
      this.effectiveBomItems = bomItems.map(it => {
        const { children, parent } = this.bomToParentAndChildrenMap.get(it.refKey) ?? { children: [], parent: null }
        const extendedItem = { ...it, children, isCollapsed: false }
        return !!parent ? { ...extendedItem, parent } : extendedItem
      })
      if (this.selected?.length) {
        this.openSelectedThumbnailPopover(this.selected[0])
      }
      this.bomInstanceKey++
    },
    clearClick () {
      this.bomSearchRequestId++
      this.bomQuery = ''
      this.highlightedInstance = 0
      this.effectiveBomItems.forEach(item => {
        item.isInSearchResults = false
      })
      this.bomInstanceKey++
      this.clearBomItemSelection()
    }
  },
  created () {
    this.$store.dispatch('user/initializePricingVisibility')
  },
  async mounted () {
    if (!this.isWidget) {
      const routeQ = this.$route?.query?.toc
      const queryParam = (typeof routeQ === 'string') ? routeQ : ''
      const partLocation = partLocationUtil.getPartLocationData(this.$route.path)
      const isPageRoute = Boolean(partLocation?.pageId)
      if (isPageRoute && queryParam) {
        this.bomQuery = queryParam
      }
    }
    this.hasSeededTocQuery = true
    if (this.bomItems?.length) {
      this.preprocessBomItemData()
      if (this.bomQuery) {
        await this.search(this.bomQuery, { fromToc: true })
      }
    }
    this.isMounting = false
    // If items arrived during the mounting window, the items watcher may have
    // returned early. Re-check once mounting completes so we don't miss running
    // the BOM search from store-backed bomQuery.
    if (this.bomItems?.length && this.bomQuery) {
      if (!this.effectiveBomItems?.length) {
        this.preprocessBomItemData()
      }
      await this.search(this.bomQuery, { fromToc: true })
    }
  },
  updated () {
    if (this.isWidget && this.isSelectingBomItemViaWidgetConfig) {
      this.setIsSelectingBomItemViaWidgetConfig(false)
    }
  }
}
</script>

<style scoped>
.navbar {
  z-index: 10;
  padding: .25rem;
  min-height: 2rem;
  flex-shrink: 0;
}
.field {
  flex-grow: 1;
}
.bomContainer {
  min-height: auto;
  flex: 1;
  display: flex;
  flex-flow: column;
  overflow: hidden;
}
.bomWrapper {
  overflow: auto;
  flex-grow: 1;
}
.table {
  min-width: 100%;
  border-bottom: 1px solid lightgrey;
  border-collapse: separate;
}
.loading {
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: center;
}
.horizontal {
  border-left: lightgray 1px solid;
}
.vertical {
  border-top: lightgray 1px solid;
}
.is-outlined {
  background-color: white !important;
  color: grey !important;
}

thead {
  position: sticky;
  position: -webkit-sticky;
  top: 0;
  background-color: white !important;
  box-shadow: 1px 0 0 0 #ccc;
  z-index: 2;
}

.widget-font {
  font-size: 0.8em;
}

@media screen and (max-width: 640px) {
  .widget-font {
    font-size: 0.7em;
  }
}
.bom-cell {
  position: relative;
}
</style>
